import { createRootRoute, Outlet, useLocation } from "@tanstack/solid-router";
import { createEffect, Suspense } from "solid-js";
import { isInGameRoute, recordGamePlayed } from "@melodimals/monetization";
import { GlassSurface } from "@/components/ui/glass-surface";
import { AlertCircle, RefreshCw } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { PlayErrorBoundary } from "@/components/play-error-boundary";

function RootErrorComponent(props: { error: unknown; reset: () => void }) {
  const { t } = useI18n();
  createEffect(() => {
    console.error("[RootErrorComponent] Caught route error:", props.error);
  });
  return (
    <div class="min-h-dvh flex items-center justify-center p-4 bg-background">
      <GlassSurface class="max-w-md w-full p-8 flex flex-col items-center text-center space-y-6">
        <div class="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertCircle class="h-8 w-8 text-destructive" />
        </div>
        <div class="space-y-2">
          <h2 class="text-2xl font-bold tracking-tight">{t(msg`Something went wrong`)}</h2>
          <p class="text-muted-foreground text-sm">
            {props.error instanceof Error
              ? props.error.message
              : t(msg`An unexpected error has occurred.`)}
          </p>
        </div>
        <div class="flex gap-3 w-full">
          <Button
            variant="outline"
            class="flex-1"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            {t(msg`Reload page`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
          >
            <RefreshCw class="h-4 w-4" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

/**
 * Counts the session's "value units" for the ads policy.
 *
 * One unit = **one completed game**, and what marks it is LEAVING a game route
 * for one that is not. This happens here, at the root, rather than in every
 * module for two reasons: a new mini-game is covered without remembering
 * anything, and leaving through the system back button counts the same as
 * leaving through the screen button.
 *
 * Without at least one unit, `adGate` denies the interstitial with
 * `no-value-unit`—exactly what prevents an ad from appearing BEFORE the first
 * game of the session.
 */
function useGamePlayedTracker() {
  const location = useLocation();
  let wasInGame = false;
  createEffect(() => {
    const inGame = isInGameRoute(location().pathname);
    if (wasInGame && !inGame) {
      recordGamePlayed();
    }
    wasInGame = inGame;
  });
}

function RootComponent() {
  useGamePlayedTracker();
  return (
    <div class="relative min-h-dvh w-full">
      {/* Red de seguridad global: cualquier error de render que escape de un
          juego cae aquí en vez de dejar la pantalla en blanco. */}
      <PlayErrorBoundary scope="root">
        <Suspense fallback={null}>
          <div class="flex flex-col min-h-dvh">
            <Outlet />
          </div>
        </Suspense>
      </PlayErrorBoundary>
    </div>
  );
}

export const rootRoute = createRootRoute({
  component: RootComponent,
  errorComponent: RootErrorComponent,
});
